/* tslint:disable:max-file-line-count */
// Vendor
import { action, computed, extendObservable, intercept, observable, runInAction } from 'mobx';

// Bernie
import { AnalyticsStore, ContextStore, ExperimentStore, SerializedData, Store } from 'bernie-core';
import { getBexApiContext } from 'bernie-graphql';
import { getErrorString } from 'bernie-logger';
import { ILocalization, Localization } from 'bernie-l10n';
import { Logger, NOOP_LOGGER } from 'bernie-logger';

// Sources
import { BexApiSource } from 'sources/bex-api-source';
import { HotelInfoSource } from 'sources/hotel-info-source';
import { HotelOffersSource } from 'sources/hotel-offers-source';
import { PoiContentSource } from 'sources/poi-content-source';
import { RateCalendarApiSource } from 'sources/rate-calendar-api-source';

import { getCreateConversationInput } from 'stores/adapters/bex-api/conversations/conversations-input-adapter';
import { InfositeQueryAdapter } from 'stores/adapters/infosite-query-adapter';
import { mapReviews } from 'stores/adapters/bex-api/reviews/reviews-adapter';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';
import { STORE_EVENTS } from 'stores/events/store-events';

// Models
import {
  ConversationsModel,
  HotelDetailsViewModel,
  HotelMapModel,
  HotelRoomsAndRatesViewModel,
  HotelViewModel,
  PropertyLocationScoreInfoModel,
  PropertyRoomScoreInfoModel,
  RateCalendarModel,
  ReferrerInfoModel,
  RegionInfoModel,
  ReviewsViewModel,
  RoomViewModel,
  SharedGuidsModel,
  TopReviewSnippetModel
} from 'models';
// Stores
import { ExtendedContextStore } from 'types/flex-definitions';
import {
  Conversations,
  CreateLodgingConversation,
  PropertyLocationScoreInfo,
  PropertyReviewType,
  PropertyRoomScoreInfo
} from '__generated__/api/types';
import { BreadcrumbsViewModel } from 'models/common/breadcrumbs-model';

import { DateUtils } from 'utils/date-utils';
import { getPropertyInfoForKeyword, ResultsCard } from 'utils/infosite-search-utils';
import { mergeProductStrings } from 'utils/infosite-utils';

// Components
import { MessagingCardProps } from 'components/common/messaging-card/messaging-card';
// Generated
import { ContextInput, Locale, PropertyReviewSort, Reviews, TranslateReview, UpvotePropertyReview } from '__generated__/api';
// Utils
import { Base64 } from 'js-base64';
import { parse, stringify } from 'query-string';

import _SortAndFilter = Reviews._SortAndFilter;

const REVIEWS_TO_RETRIEVE = 10;

interface CurrentHotelStoreDefaults {
  conversations: ConversationsModel;
  criticalReviews: ReviewsViewModel;
  filteredReviews: ReviewsViewModel;
  findAnswersSearchData: ResultsCard[];
  hotelId?: number;
  l10n: ILocalization;
  offerSearchData: object;
  positiveReviews: ReviewsViewModel;
  reviewIdsUpvoted: string[];
  reviews: ReviewsViewModel;
  sortAndFilter: _SortAndFilter[];
  poiData: object;
  propertyRoomScoreInfo: PropertyRoomScoreInfoModel[];
  propertyLocationScoreInfo: PropertyLocationScoreInfoModel;
  selectedGalleryCategoryId: string;
  topReviewSnippet: TopReviewSnippetModel;
}

export class CurrentHotelStore extends Store {
  private PAST_PADDING: number = 90;
  private FUTURE_PADDING: number = 150;
  private SIMILAR_HOTELS_TO_SHOW: number = 10;

  constructor(
    private hotelInfoSource: HotelInfoSource,
    private hotelOffersSource: HotelOffersSource,
    private bexApiSource: BexApiSource,
    private rateCalendarSource: RateCalendarApiSource,
    private poiContentSource: PoiContentSource,
    state: SerializedData = {},
    l10n: ILocalization,
    logger: Logger = NOOP_LOGGER
  ) {
    super(state, logger);

    this.l10n = l10n;

    this.searchCriteria = new SearchCriteriaModel();
    this.numberInterceptor('hotelId');
  }

  public hydrate(data: SerializedData): void {
    if (data && data.hotel && data.hotel.code === 'INACTIVE_HOTEL_ID') {
      window.location.replace(`https://${window.location.host}/Hotels`);
    }

    delete data.hotelInfoSource;
    delete data.hotelOffersSource;
    delete data.bexApiSource;
    delete data.shouldFetchInfo;
    delete data.shouldFetchOffers;

    this.searchCriteria.hydrate(data.searchCriteria || {});
    delete data.searchCriteria;

    Object.assign(this, data);
  }

  // setup mobx interceptor to wrap dates in a method to format data passed to setter
  public numberInterceptor(prop): void {
    if (typeof prop === 'string') {
      intercept(this, prop as any, change => {
        if (change.newValue) {
          const newValue = this.sanitizeNumber(change.newValue);

          if (newValue) {
            change.newValue = newValue;

            return change;
          }
        }
      });
    }
  }

  public sanitizeNumber(number): number | string {
    try {
      const cleanNumber = typeof number === 'number' ? number : parseInt(number, 10);

      if (Number.isNaN(cleanNumber)) {
        throw new Error(`Invalid Number: cannot parse ${number} into a Number`);
      }

      return cleanNumber;
    } catch (error) {
      this.logErrorEvent(STORE_EVENTS.SANATIZE_NUMNBER_ERROR, getErrorString(error));

      return '';
    }
  }

  public static DEFAULTS: CurrentHotelStoreDefaults = {
    conversations: {},
    findAnswersSearchData: [],
    hotelId: null,
    l10n: new Localization('en_US'),
    offerSearchData: {},
    reviewIdsUpvoted: [],
    propertyRoomScoreInfo: [],
    propertyLocationScoreInfo: {},
    reviews: {
      cleanliness: null,
      count: null,
      localizedCount: null,
      localizedCountWithLabel: null,
      fetchedEmptyReviews: null,
      hotelCondition: null,
      overallRating: null,
      amenityScore: null,
      serviceAndStaff: null,
      superlative: null,
      totalReviewsRequested: 0,
      userReviews: []
    },
    positiveReviews: {
      cleanliness: null,
      count: null,
      fetchedEmptyReviews: null,
      hotelCondition: null,
      overallRating: null,
      amenityScore: null,
      serviceAndStaff: null,
      superlative: null,
      totalReviewsRequested: 0,
      userReviews: []
    },
    criticalReviews: {
      cleanliness: null,
      count: null,
      fetchedEmptyReviews: null,
      hotelCondition: null,
      overallRating: null,
      amenityScore: null,
      serviceAndStaff: null,
      superlative: null,
      totalReviewsRequested: 0,
      userReviews: []
    },
    filteredReviews: {
      cleanliness: null,
      count: null,
      fetchedEmptyReviews: null,
      hotelCondition: null,
      overallRating: null,
      amenityScore: null,
      serviceAndStaff: null,
      superlative: null,
      totalReviewsRequested: 0,
      userReviews: []
    },
    poiData: {},
    sortAndFilter: [],
    selectedGalleryCategoryId: 'all',
    topReviewSnippet: {
      superlative: null,
      ratingOverall: null,
      text: null,
      author: null,
      reviewerVerified: false
    }
  };

  @observable
  public l10n: ILocalization;
  @observable
  public breadcrumbs: BreadcrumbsViewModel;
  @observable
  public toolbar: any;
  @observable
  public hotel: HotelDetailsViewModel;
  @observable
  public map: HotelMapModel;
  @observable
  public offers: HotelRoomsAndRatesViewModel;
  @observable
  public reviewIdsUpvoted: string[];
  @observable
  public reviews: ReviewsViewModel = CurrentHotelStore.DEFAULTS.reviews;
  public hasPreFetchedReviews: boolean = false;
  @observable
  public criticalReviews: ReviewsViewModel = CurrentHotelStore.DEFAULTS.criticalReviews;
  @observable
  public conversations: ConversationsModel;
  @observable
  public positiveReviews: ReviewsViewModel = CurrentHotelStore.DEFAULTS.positiveReviews;
  @observable
  public reviewScoreFilters: number[];
  @observable
  public reviewTripReasonFilters: string[];
  @observable
  public reviewTravelCompanionFilters: string[];
  @observable
  public reviewLanguageFilters: string;
  @observable
  public filteredReviews: ReviewsViewModel = CurrentHotelStore.DEFAULTS.filteredReviews;
  @observable
  public topReviewSnippet: TopReviewSnippetModel = CurrentHotelStore.DEFAULTS.topReviewSnippet;
  public hasPrefetchedTopReview: boolean = false;
  @observable
  public propertyRoomScoreInfo: PropertyRoomScoreInfoModel[] = CurrentHotelStore.DEFAULTS.propertyRoomScoreInfo;
  @observable
  public sortAndFilter: _SortAndFilter[] = CurrentHotelStore.DEFAULTS.sortAndFilter;
  @observable
  public propertyLocationScoreInfo: PropertyLocationScoreInfoModel = CurrentHotelStore.DEFAULTS.propertyLocationScoreInfo;

  @observable
  public hotelId: number;
  @observable
  public isLoading: boolean;
  @observable
  public isFailure: boolean;
  @observable
  public shouldFetch: boolean;
  @observable
  public shouldFetchOffers: boolean;
  @observable
  public shouldFetchTopReview: boolean;
  @observable
  public isHotelInfoCached: boolean;

  @observable
  public loadingHotel: boolean;
  @observable
  public loadingOffers: boolean;
  @observable
  public defaultedDates: boolean;
  @observable
  public loadingETPData: boolean;
  @observable
  public loadingConversations: boolean;
  @observable
  public creatingConversation: boolean;
  @observable
  public similarHotels: HotelViewModel[];

  @observable
  public offerSearchData: any;

  @observable
  public findAnswersSearchData: ResultsCard[];

  @observable
  public travelAd: any;
  @observable
  public requests: any;
  @observable
  public messagingCardModel: MessagingCardProps;
  @observable
  public viewsCountMessagingCard: string;
  @observable
  public shouldFetchRoomScore: boolean = false;
  @observable
  public shouldFetchLocationScore: boolean = false;
  @observable
  public freeLxMessagingCard: MessagingCardProps;
  @observable
  public regionInfo: RegionInfoModel;
  @observable
  public referrerInfo: ReferrerInfoModel;
  @observable
  public sharedGuids: SharedGuidsModel;

  @observable
  public photoGalleryIndex: number = 0;
  @observable
  public selectedGalleryCategoryId: string = CurrentHotelStore.DEFAULTS.selectedGalleryCategoryId;

  @observable
  public rateCalendar: RateCalendarModel;
  @observable.ref
  public searchCriteria: SearchCriteriaModel;

  @observable
  public poiData: object = CurrentHotelStore.DEFAULTS.poiData;

  private _fetchingHotel: number;

  @action
  public setSearchResultAsCurrentHotel(searchResultHotel): void {
    if (this.hotelId !== searchResultHotel.id) {
      this.shouldFetch = true;

      const newHotel = {
        defaultData: true,
        gallery: {
          images: [
            {
              caption: searchResultHotel.thumbnail.caption,
              src: searchResultHotel.thumbnail.src,
              thumbnailSrc: searchResultHotel.thumbnail.thumbnailSrc,
              fallBackImage: searchResultHotel.thumbnail.fallBackImage
            }
          ],
          thumbnailGalleryEnabled: false,
          total: 1
        },
        hotelId: searchResultHotel.id,
        hotelName: searchResultHotel.hotelName || searchResultHotel.name || '',
        hotelBrandId: searchResultHotel.hotelBrandId,
        id: searchResultHotel.id,
        overview: {
          content: '',
          staticMapSrc: null,
          latitude: '0',
          longitude: '0'
        },
        reviews: {},
        shortlist: searchResultHotel.shortlist,
        stars: searchResultHotel.stars,
        starRatingIcon: searchResultHotel.starRatingIcon
      };

      this.travelAd = searchResultHotel.travelAd;
      this.hotel = newHotel;
      this.clearReviews();
    }

    this.toolbar = {
      name: searchResultHotel.name,
      stars: searchResultHotel.stars
    };
  }

  @action
  public updateSearchCriteria(criteria: SearchCriteriaModel): void {
    this.searchCriteria = criteria;
  }

  @action
  public clearDatelessContexts(context: ExtendedContextStore): void {
    if (this.hotel) {
      delete this.hotel.propertySummaryDescription;

      if (this.hotel.additionalInfo) {
        delete this.hotel.additionalInfo.propertyAlternativeName;
      }
    }

    if (context.footer) {
      delete context.footer;
    }
  }

  @action
  public setHotelId(hotelId) {
    if (hotelId !== this.hotelId) {
      this.shouldFetch = true;
      this.hotelId = hotelId;
    }
  }

  @action
  public populateRoomScoreInRooms(rooms: RoomViewModel[], roomScoreInfo: PropertyRoomScoreInfoModel[]): RoomViewModel[] {
    roomScoreInfo.forEach(value => {
      rooms.forEach(room => {
        if (room.roomTypeCode === value.id) {
          room.roomScoreInfo = value;
        }
      });
    });

    return rooms;
  }

  @action
  public resetFlyinMessage(): void {
    if (this.offers && this.offers.flyInModel) {
      this.offers.flyInModel.text = '';
      this.offers.flyInModel.icon = '';
    }
  }

  @computed
  get roomCount(): number {
    return this.offers && this.offers.rooms && this.offers.rooms.length;
  }

  private mapOmnitureData(analyticsStore: AnalyticsStore, experiments: ExperimentStore) {
    if (analyticsStore && !this.loadingOffers) {
      const infoOmnitureData = (this.hotel && this.hotel.omnitureData) || null;
      const offers = this.massageOmnitureDataStructure(this.offers);
      const offersOmnitureData = (offers && offers.omnitureData) || null;

      if (infoOmnitureData && offersOmnitureData) {
        const infoOmnitureCopy = JSON.parse(JSON.stringify(infoOmnitureData));
        const offersOmnitureCopy = JSON.parse(JSON.stringify(offersOmnitureData));
        mergeProductStrings(infoOmnitureCopy, offersOmnitureCopy);
        if (this.isHotelInfoCached) {
          infoOmnitureCopy.traceId = offersOmnitureCopy.omnitureProperties.traceId;
        }

        // Explicitly override products field to work around falsy data.
        if (offersOmnitureCopy.omnitureProperties.adobeMappedData && infoOmnitureCopy.adobeMappedData) {
          offersOmnitureCopy.omnitureProperties.adobeMappedData['products'] = infoOmnitureCopy.adobeMappedData['products'];
        }

        const analyticsPayload = analyticsStore.mergeOmnitureData(infoOmnitureCopy, offersOmnitureCopy);

        if (experiments) {
          const { PWA_ANALYTICS_REFERRER_URL } = experiments.exposures;
          if (PWA_ANALYTICS_REFERRER_URL && PWA_ANALYTICS_REFERRER_URL.bucket === 1) {
            const encodedReferrerUrl = this.searchCriteria.referrerUrl;
            if (encodedReferrerUrl) {
              analyticsPayload.adobeMappedData.referrer = Base64.decode(encodedReferrerUrl);
            }
          }
        }

        return analyticsPayload;
      }
    }

    return null;
  }

  @action
  public fetch(analyticsStore: AnalyticsStore, context: ContextStore, experiments: ExperimentStore, additionalCriteria: any = {}) {
    const { hotelId, searchCriteria } = this;

    const modelData = (searchCriteria && searchCriteria.toJSON()) || {};
    const criteriaJson = {
      ...InfositeQueryAdapter.parseOut(modelData, this.l10n),
      ...modelData
    };

    this.logParallelCalls(hotelId); // for logging dups

    this.shouldFetch = false;
    this.isLoading = true;
    this.isFailure = false;
    this.photoGalleryIndex = 0;
    this.selectedGalleryCategoryId = CurrentHotelStore.DEFAULTS.selectedGalleryCategoryId;

    const infoPromise = this.fetchInfo(context, criteriaJson, additionalCriteria, hotelId);
    const offerPromise = this.fetchOffers(context, criteriaJson, additionalCriteria, hotelId);

    return Promise.all([infoPromise, offerPromise])
      .then(
        () =>
          new Promise(resolve => {
            runInAction('updating shared current hotel', () => {
              this.shouldFetch = false;
              this.isLoading = false;
              this.isFailure = false;

              if (analyticsStore && experiments) {
                const { PWA_PDP_CLIENT_ANALYTICS } = experiments.exposures;
                if (PWA_PDP_CLIENT_ANALYTICS && PWA_PDP_CLIENT_ANALYTICS.bucket === 1) {
                  const payload = this.mapOmnitureData(analyticsStore, experiments);
                  analyticsStore.publishClientSidePayload([payload]);
                }
              }
              resolve();
            });
          })
      )
      .catch(
        () =>
          new Promise(resolve => {
            runInAction('updating current hotel with failure', () => {
              this.shouldFetch = false;
              this.isFailure = true;
              this.isLoading = false;
              resolve();
            });
          })
      );
  }

  private logParallelCalls(hotelId: number) {
    if (this.isLoading === true) {
      // overlapping calls in progress - logging to monitor if issue is widespread
      const message =
        'Overlapping calls in progress. CurrentHotelStore is fetching in parallel, ' +
        `firstHotelId=${this._fetchingHotel} secondHotelId=${hotelId}`;
      this.logger.logEvent(STORE_EVENTS.CURRENT_HOTEL_STORE_PARALLEL_FETCH, message);
    }
    this._fetchingHotel = hotelId;
  }

  private logErrorEvent(name, err) {
    this.logger.logEvent(name, getErrorString(err));
  }

  private shouldFetchInfo(hotelId, criteria): boolean {
    return (
      hotelId && // has valid hotelId for fetch
      (this.shouldFetch ||
      this.isFailure || // fetch is forced
      !this.hotel ||
      !this.hotel.id ||
      hotelId !== this.hotel.id || // current hotel exist but mismatches ID
      Boolean(this.hotel.defaultData) || // defaulted data
        (criteria && criteria.amenities && criteria.amenities.length > 0))
    );
  }

  @action
  public fetchInfo(context: ContextStore, criteria: any = {}, additionalData: any = {}, hotelId: number): Promise<any> {
    // since hotel info is static, this method is selective about fetching hotel info
    this.isHotelInfoCached = false;
    if (!this.shouldFetchInfo(hotelId, criteria)) {
      this.isHotelInfoCached = true;

      return Promise.resolve();
    }

    this.loadingHotel = true;

    if (this.hotel && hotelId !== this.hotel.id) {
      this.hotel = null;
      this.clearReviews();
    }

    const requestData = { ...criteria, ...additionalData };

    return this.hotelInfoSource
      .fetch(context, requestData, hotelId)
      .then(data => {
        runInAction('update current hotel from response', () => {
          this.loadingHotel = false;
          this.hotel = data;
          this.toolbar = {
            name: this.hotel.name,
            stars: this.hotel.stars
          };

          if (!criteria.regionId && data.region) {
            criteria.regionId = data.region.id;
            criteria.destination = data.region.name;
          }
        });
        this.setIntentMediaParams(data.intentMedia, data.overview);

        return data;
      })
      .catch(error => {
        runInAction('recovering from hotel info call failure', () => {
          this.loadingHotel = false;
          this.logErrorEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(error));
        });

        return {
          error
        };
      });
  }

  @action
  public fetchOffers(context: ContextStore, criteria: any = {}, additionalData: any = {}, hotelId: number): Promise<any> {
    // Note: always fetches offers -- does not check if a fetch is needed
    this.loadingOffers = true;
    this.defaultedDates = false;
    this.shouldFetchOffers = false;
    this.messagingCardModel = null;
    this.viewsCountMessagingCard = null;

    // hotelId is optional, otherwise it defaults to current hotelId
    if (!hotelId) {
      hotelId = this.hotelId;
    }

    const travelAd = this.travelAd;
    const requestData = { ...criteria, ...additionalData };

    return this.hotelOffersSource
      .fetch(context, requestData, hotelId, travelAd)
      .then(data => {
        runInAction('update current hotel offers from response', () => {
          this.loadingOffers = false;
          this.offers = null;

          this.massageOmnitureDataStructure(data);

          if (data && !data.omnitureData && this.logger && typeof this.logger.logEvent === 'function') {
            const error = new Error('Empty omnitureData returned with offers');
            this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(error));
          }

          this.offers = data;
        });

        const defaultRequestFields = data.defaultRequestFields;

        if (defaultRequestFields) {
          this.defaultedDates = true;
        }

        if (data.messagingCardModel) {
          this.messagingCardModel = data.messagingCardModel;
        }

        if (data.viewsCountMessagingCard) {
          this.viewsCountMessagingCard = data.viewsCountMessagingCard;
        }

        return data;
      })
      .catch(error => {
        runInAction('recovering from hotel offers call breakdown', () => {
          this.loadingOffers = false;
          this.isFailure = true;
          this.logErrorEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(error));
        });

        return {
          error
        };
      });
  }

  private massageOmnitureDataStructure(data: any): any {
    // TODO fix this structure in the BFF
    if (data && !data.omnitureData && data.omnitureProperties) {
      data.omnitureData = {
        omnitureProperties: data.omnitureProperties
      };

      delete data.omnitureProperties;
    }

    return data;
  }

  @action
  public setOfferData(offerData): void {
    this.offerSearchData = Object.assign({}, this.offerSearchData, offerData);
  }

  @action
  public setBreadcrumbsModel(regionList: any[]): void {
    let breadcrumbsModule;
    const breadcrumbsList = [];

    if (regionList.length >= 1) {
      breadcrumbsModule = regionList[0].modules.find(module => module.name === 'breadcrumbs');
    }

    if (
      breadcrumbsModule &&
      breadcrumbsModule.model &&
      breadcrumbsModule.model.breadcrumbs &&
      breadcrumbsModule.model.breadcrumbs.length > 0
    ) {
      breadcrumbsModule.model.breadcrumbs.forEach(crumb => {
        breadcrumbsList.push({ text: crumb.text, url: crumb.url, fullUrl: crumb.fullUrl, index: crumb.index });
      });
    }

    const newBreadcrumb = {
      crumbs: breadcrumbsList
    };

    this.breadcrumbs = newBreadcrumb;
  }

  @action
  public clearRoomsFromOffers(): void {
    return this.offers && this.offers.rooms && (this.offers.rooms = null);
  }

  @action
  public getOfferData() {
    const { startDate, endDate, adults, children } = this.offerSearchData;

    return {
      startDate,
      endDate,
      adults,
      children
    };
  }

  @action
  public clearRecommendedOfferFromOffers(): void {
    return this.offers && this.offers.recommendedOffer && (this.offers.recommendedOffer = null);
  }

  @action
  public clearReviews(): void {
    runInAction('clear reviews', () => {
      this.hasPreFetchedReviews = false;
      this.reviews = CurrentHotelStore.DEFAULTS.reviews;
      this.criticalReviews = CurrentHotelStore.DEFAULTS.reviews;
      this.positiveReviews = CurrentHotelStore.DEFAULTS.reviews;
    });
  }

  @action
  public fetchReviews(
    context: ContextStore,
    hotelId: number,
    includeRatingsOnlyReviews?: boolean,
    reviewType?: PropertyReviewType
  ): Promise<any> {
    const contextInput: ContextInput = getBexApiContext(context);
    const reviews = this.getReviewsByReviewType(reviewType);
    const nextIndex = (reviews && reviews.userReviews && reviews.userReviews.length) || 0;

    const variables: Reviews.Variables = {
      context: contextInput,
      propertyId: hotelId.toString(),
      sortBy: this.getSortByReviewType(reviewType),
      filters: {
        includeRecentReviews: this.searchCriteria.startDate != null && this.searchCriteria.startDate.length !== 0,
        includeRatingsOnlyReviews,
        reviewType
      },
      pagination: {
        startingIndex: nextIndex,
        size: REVIEWS_TO_RETRIEVE
      },
      withTop: Boolean(this.shouldFetchTopReview && !this.hasPrefetchedTopReview)
    };

    return this.bexApiSource
      .fetchReviews(variables)
      .then(({ data }) => {
        runInAction('update bex api hotel reviews from response', () => {
          if (data.propertyInfo && data.propertyInfo.reviewInfo) {
            this.hasPreFetchedReviews = true;
            switch (reviewType) {
              case PropertyReviewType.POSITIVE:
                this.positiveReviews = mapReviews(this.positiveReviews, data.propertyInfo.reviewInfo, nextIndex + REVIEWS_TO_RETRIEVE);
                this.positiveReviews.fetchedEmptyReviews =
                  data.propertyInfo.reviewInfo.reviews && data.propertyInfo.reviewInfo.reviews.length === 0;
                break;
              case PropertyReviewType.CRITICAL:
                this.criticalReviews = mapReviews(this.criticalReviews, data.propertyInfo.reviewInfo, nextIndex + REVIEWS_TO_RETRIEVE);
                this.criticalReviews.fetchedEmptyReviews =
                  data.propertyInfo.reviewInfo.reviews && data.propertyInfo.reviewInfo.reviews.length === 0;
                break;
              default:
                this.reviews = mapReviews(this.reviews, data.propertyInfo.reviewInfo, nextIndex + REVIEWS_TO_RETRIEVE);
                if (data.propertyInfo.reviewInfo.sortAndFilter && this.sortAndFilter.length === 0) {
                  this.sortAndFilter = data.propertyInfo.reviewInfo.sortAndFilter.sortAndFilter;
                }
                break;
            }
          }
          if (data.propertyInfo && data.propertyInfo.topReview) {
            this.hasPrefetchedTopReview = true;
            this.topReviewSnippet = data.propertyInfo.topReview.reviews[0];
          }
        });
      })
      .catch(error => {
        runInAction('recovering from bex api hotel reviews call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.BEX_API_CURRENT_HOTEL_STORE_FETCH_ERROR, variables, getErrorString(error));
        });

        return {
          error
        };
      });
  }

  @action
  public fetchPropertyLocationScoreInfo(context: ContextStore, propertyId: number): Promise<any> {
    const contextInput: ContextInput = getBexApiContext(context);
    const variables: PropertyLocationScoreInfo.Variables = {
      context: contextInput,
      propertyIds: [propertyId.toString()]
    };

    return this.bexApiSource
      .fetchPropertyLocationScoreInfo(variables)
      .then(({ data }) => {
        runInAction('update propertyLocationScoreInfo from response', () => {
          if (
            data.propertyReviewAggregates &&
            data.propertyReviewAggregates.propertySummaries &&
            data.propertyReviewAggregates.propertySummaries.length
          ) {
            this.propertyLocationScoreInfo = data.propertyReviewAggregates.propertySummaries[0].locationScore;
          }
        });
      })
      .catch(error => {
        runInAction('recovering from bex api property info location score call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, variables, getErrorString(error));
        });

        return {
          error
        };
      });
  }

  @action
  public fetchTranslatedReview(context: ContextStore, hotelId: number, reviewId: string, targetLocale: Locale): Promise<any> {
    const contextInput: ContextInput = getBexApiContext(context);

    const variables: TranslateReview.Variables = {
      context: contextInput,
      hotelId: hotelId.toString(),
      reviewId,
      targetLocale: targetLocale.toString()
    };

    return this.bexApiSource.fetchTranslatedReview(variables).then(({ data }) => {
      const review = data.propertyInfo.review;

      return { review };
    });
  }

  @action
  public fetchPropertyRoomScoreInfo(context: ContextStore, propertyId: number): Promise<any> {
    const contextInput: ContextInput = getBexApiContext(context);
    const variables: PropertyRoomScoreInfo.Variables = {
      context: contextInput,
      propertyIds: [propertyId.toString()]
    };
    this.propertyRoomScoreInfo = null;

    return this.bexApiSource
      .fetchPropertyRoomScoreInfo(variables)
      .then(({ data }) => {
        if (
          data.propertyReviewAggregates &&
          data.propertyReviewAggregates.propertySummaries &&
          data.propertyReviewAggregates.propertySummaries.length
        ) {
          this.propertyRoomScoreInfo = data.propertyReviewAggregates.propertySummaries[0].roomsScore;
        }
      })
      .catch(error => {
        runInAction('recovering from bex api property info room score call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, variables, getErrorString(error));
        });

        return {
          error
        };
      });
  }

  @action
  public setReviewsScoreFilter(scores) {
    if (scores !== this.reviewScoreFilters) {
      this.filteredReviews = CurrentHotelStore.DEFAULTS.filteredReviews;
      this.reviewScoreFilters = scores;
    }
  }

  @action
  public setTravelerTypeFilters(travelCompanionFilters, tripReasonFilters) {
    if (travelCompanionFilters !== this.reviewTravelCompanionFilters || tripReasonFilters !== this.reviewTripReasonFilters) {
      this.filteredReviews = CurrentHotelStore.DEFAULTS.filteredReviews;
      this.reviewTravelCompanionFilters = travelCompanionFilters;
      this.reviewTripReasonFilters = tripReasonFilters;
    }
  }

  @action
  public setLanguageTypeFilters(languageFilter?: string) {
    if (languageFilter !== this.reviewLanguageFilters) {
      this.filteredReviews = CurrentHotelStore.DEFAULTS.filteredReviews;
      this.reviewLanguageFilters = languageFilter;
    }
  }

  @computed
  get checkReviewsFiltersApplied(): boolean {
    return (
      (this.reviewScoreFilters && this.reviewScoreFilters.length > 0) ||
      (this.reviewTravelCompanionFilters && this.reviewTravelCompanionFilters.length > 0) ||
      (this.reviewTripReasonFilters && this.reviewTripReasonFilters.length > 0) ||
      (this.reviewLanguageFilters && typeof this.reviewLanguageFilters === 'string')
    );
  }

  @action
  public fetchFilteredReviews(context: ContextStore, hotelId: number, includeRatingsOnlyReviews?: boolean): Promise<any> {
    const contextInput: ContextInput = getBexApiContext(context);
    const nextIndex = (this.filteredReviews && this.filteredReviews.userReviews && this.filteredReviews.userReviews.length) || 0;

    const variables: Reviews.Variables = {
      context: contextInput,
      propertyId: hotelId.toString(),
      sortBy: PropertyReviewSort.NEWEST_TO_OLDEST,
      filters: {
        includeRecentReviews: this.searchCriteria.startDate != null && this.searchCriteria.startDate.length !== 0,
        includeRatingsOnlyReviews,
        ratings: this.reviewScoreFilters,
        tripReason: this.reviewTripReasonFilters,
        travelCompanion: this.reviewTravelCompanionFilters,
        language: typeof this.reviewLanguageFilters === 'string' ? this.reviewLanguageFilters : undefined
      },
      pagination: {
        startingIndex: nextIndex,
        size: REVIEWS_TO_RETRIEVE
      },
      withTop: false
    };

    return this.bexApiSource
      .fetchReviews(variables)
      .then(({ data }) => {
        runInAction('update bex api hotel reviews from response', () => {
          if (data.propertyInfo && data.propertyInfo.reviewInfo) {
            this.filteredReviews = mapReviews(this.filteredReviews, data.propertyInfo.reviewInfo, nextIndex + REVIEWS_TO_RETRIEVE);
            this.filteredReviews.fetchedEmptyReviews =
              data.propertyInfo.reviewInfo.reviews && data.propertyInfo.reviewInfo.reviews.length === 0;
          }
        });
      })
      .catch(error => {
        runInAction('recovering from bex api hotel reviews call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.BEX_API_CURRENT_HOTEL_STORE_FETCH_ERROR, variables, getErrorString(error));
        });

        return {
          error
        };
      });
  }

  private getSortByReviewType(reviewType) {
    const sortByReviewType = {};

    sortByReviewType[PropertyReviewType.POSITIVE] = PropertyReviewSort.HIGHEST_TO_LOWEST_RATED;
    sortByReviewType[PropertyReviewType.CRITICAL] = PropertyReviewSort.LOWEST_TO_HIGHEST_RATED;

    return sortByReviewType[reviewType] || PropertyReviewSort.NEWEST_TO_OLDEST;
  }

  private getReviewsByReviewType(reviewType?: PropertyReviewType): ReviewsViewModel {
    if (!this.positiveReviews) {
      this.positiveReviews = CurrentHotelStore.DEFAULTS.reviews;
      extendObservable(this.positiveReviews, { fetchedEmptyReviews: undefined });
    }

    if (!this.criticalReviews) {
      this.criticalReviews = CurrentHotelStore.DEFAULTS.reviews;
      extendObservable(this.criticalReviews, { fetchedEmptyReviews: undefined });
    }

    const reviewsByType = {};

    reviewsByType[PropertyReviewType.POSITIVE] = this.positiveReviews;
    reviewsByType[PropertyReviewType.CRITICAL] = this.criticalReviews;

    return reviewsByType[reviewType] || this.reviews;
  }

  @action
  public setCurrentGalleryIndex(index) {
    if (index !== this.photoGalleryIndex) {
      this.photoGalleryIndex = index;
    }
  }

  @action
  public setSelectedGalleryCategoryId(selectedGalleryCategoryId) {
    if (selectedGalleryCategoryId !== this.selectedGalleryCategoryId) {
      this.selectedGalleryCategoryId = selectedGalleryCategoryId;
    }
  }

  @computed
  get selectedGalleryCategory() {
    if (
      this.hotel &&
      this.hotel.gallery &&
      this.hotel.gallery.categories &&
      this.hotel.gallery.categories.length > 0 &&
      this.selectedGalleryCategoryId
    ) {
      const category = this.hotel.gallery.categories.find(x => x.id === this.selectedGalleryCategoryId);
      if (category) {
        return category;
      }
    }

    return null;
  }

  @action
  public fetchHISRates(l10n, requestStartDate, requestEndDate, adults, children, hotelId, context) {
    const { startDate, endDate } = this.resolveSearchDates(l10n, requestStartDate, requestEndDate);

    return this.rateCalendarSource
      .fetchHIS(context, { startDate, endDate, adults, children }, hotelId)
      .then(res => {
        if (res && res.datePickerContent) {
          this.rateCalendar = res;
        } else {
          this.rateCalendar = {};
        }

        return this.rateCalendar;
      })
      .catch(error => {
        runInAction('rate calendar call failure', () => {
          this.logErrorEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(error));
        });
      });
  }

  @action
  public fetchPoiContent(context) {
    if (Object.keys(this.poiData).length > 0) {
      return;
    }
    this.hotel.mapModel.markers.forEach(marker => {
      if (marker.sheet) {
        const markerId = marker.sheet.id.toString();
        this.poiData[markerId] = {};
      }
    });
    this.poiContentSource
      .fetchPoi(context, Object.keys(this.poiData))
      .then(response => {
        const destinationList = response.destinations;
        destinationList.forEach(poi => {
          this.poiData[poi.id].description = poi.description;
          if (poi.image) {
            this.poiData[poi.id].photoUrl = poi.image.imageURL;
            this.poiData[poi.id].photoAlt = poi.image.altText;
          }
        });
      })
      .catch(error => {
        runInAction('poi content call failure', () => {
          this.logErrorEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(error));
        });
      });
  }

  @action
  public clearRates() {
    this.rateCalendar = {};
  }

  @action
  public getUpdatedBackToHsrUrl(formData) {
    const { startDate, endDate, adults, children } = formData;
    const { backToHsrUrl } = this.offers.infositeContext;
    const [hsrPathName, hsrQuery] = backToHsrUrl.split('?');
    const updatedQuery = Object.assign({}, parse(hsrQuery), {
      selected: this.hotelId,
      startDate,
      endDate,
      adults,
      children
    });

    return `${hsrPathName}?${stringify(updatedQuery)}`;
  }

  @computed
  get hsrQueryValues() {
    if (this.offers && this.offers.infositeContext) {
      const { backToHsrUrl } = this.offers.infositeContext;
      const hsrQuery = backToHsrUrl.split('?')[1];
      const searchQuery = parse(hsrQuery);
      searchQuery.selected = this.hotelId;

      delete searchQuery.adults;
      delete searchQuery.children;
      delete searchQuery.startDate;
      delete searchQuery.endDate;

      return searchQuery;
    }

    return {};
  }

  private resolveSearchDates(l10n, requestStartDate, requestEndDate) {
    const dateUtils = new DateUtils(l10n);

    const today = dateUtils.getToday();
    const endDate = dateUtils.getNDaysFrom(requestEndDate, this.FUTURE_PADDING);
    const daysBetweenTodayAndRequestStartDate = dateUtils.getDaysBetween(today, requestStartDate);
    let startDate;
    if (daysBetweenTodayAndRequestStartDate <= this.PAST_PADDING && daysBetweenTodayAndRequestStartDate >= 0) {
      startDate = today;
    } else {
      startDate = dateUtils.getNDaysFrom(requestStartDate, -this.PAST_PADDING);
    }

    return { startDate, endDate };
  }

  public setIntentMediaParams(intentMedia, overview) {
    if (intentMedia) {
      const {
        machineId,
        machineIdHashed,
        publisherSessionId,
        publisherSessionIdHashed,
        publisherUserIdHashed,
        referrerChannel,
        referrerSource
      } = intentMedia;
      this.sharedGuids = { machineId, machineIdHashed, publisherSessionId, publisherSessionIdHashed, publisherUserIdHashed };
      this.referrerInfo = { referrerChannel, referrerSource };
    }
    if (overview) {
      const { city, state, countryCode } = overview;
      this.regionInfo = { city, state, countryCode };
    }
  }

  @action
  public fetchSimilarHotels(context: ContextStore) {
    this.similarHotels = null;

    if (!this.hotelId || !this.searchCriteria || !this.searchCriteria.regionId) {
      return Promise.resolve();
    }

    const criteriaWithParsedGuests = SearchQueryAdapter.parseOut(this.searchCriteria && this.searchCriteria.toJSON());

    const similarHotelsCriteria = {
      ...criteriaWithParsedGuests,
      count: this.SIMILAR_HOTELS_TO_SHOW
    };

    if (!this.searchCriteria.star) {
      similarHotelsCriteria.star = '10,20,30,40,50';
    }

    return this.hotelInfoSource
      .fetchSimilarHotels(context, similarHotelsCriteria, this.hotelId)
      .then(data => {
        runInAction('update current hotel similarHotels from response', () => {
          this.similarHotels = data;
        });
      })
      .catch(error => {
        runInAction('similar hotels call failure', () => {
          this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(error));
        });
      });
  }

  @action
  public fetchConversations(propertyId: string) {
    this.loadingConversations = true;
    const variables: Conversations.Variables = {
      propertyId
    };

    if (String(this.hotelId) === this.conversations.propertyId) {
      this.loadingConversations = false;

      return Promise.resolve();
    }
    this.conversations = {};

    return this.bexApiSource
      .fetchConversations(variables)
      .then(({ data }) => {
        runInAction('update conversations from response', () => {
          this.loadingConversations = false;
          const { lodgingConversations } = data;
          this.conversations = { ...this.conversations, propertyId, viewModel: lodgingConversations };
        });
      })
      .catch(error => {
        runInAction('recovering from conversations fetch failure', () => {
          this.loadingConversations = false;
          this.logger.logEvent(STORE_EVENTS.BEX_API_CURRENT_HOTEL_STORE_FETCH_ERROR, variables, getErrorString(error));
        });
      });
  }

  @action
  public createConversation(
    message: string,
    propertyId: string,
    name: string,
    emailAddress: string,
    searchCriteria: SearchCriteriaModel,
    createdAt: string
  ) {
    this.creatingConversation = true;
    const variables: CreateLodgingConversation.Variables = getCreateConversationInput(
      message,
      searchCriteria,
      createdAt,
      propertyId,
      name,
      emailAddress
    );

    return this.bexApiSource
      .createLodgingConversation(variables)
      .then(({ data }) => {
        runInAction('update conversation from response', () => {
          this.creatingConversation = false;
          this.conversations = {
            ...this.conversations,
            propertyId,
            viewModel: {
              ...this.conversations.viewModel,
              ...data.createLodgingConversation
            }
          };
        });
      })
      .catch(error => {
        runInAction('recovering from conversation create failure', () => {
          this.creatingConversation = false;
          this.logger.logEvent(STORE_EVENTS.BEX_API_CURRENT_HOTEL_STORE_FETCH_ERROR, variables, getErrorString(error));
        });
      });
  }

  @action
  public clearCreateConversationResult() {
    delete this.conversations.viewModel.result;
    delete this.conversations.viewModel.alert;
    delete this.conversations.viewModel.action;
  }

  @action
  public getPropertyInfoForKeyword(keyword: string) {
    if (keyword == null || keyword.length === 0) {
      this.clearfindAnswersSearchData();
    }

    this.findAnswersSearchData = getPropertyInfoForKeyword(keyword, this.hotel, this.offers);
  }

  @action
  public clearfindAnswersSearchData(): void {
    this.findAnswersSearchData = [];
  }

  @action
  public upvoteReviewTotalThanks(reviewId: string) {
    const variables: UpvotePropertyReview.Variables = {
      reviewId
    };

    const review = this.reviews.userReviews.find(x => x.reviewId === reviewId);
    const positiveReview = this.positiveReviews.userReviews.find(x => x.reviewId === reviewId);
    const criticalReview = this.criticalReviews.userReviews.find(x => x.reviewId === reviewId);
    const filteredReview = this.filteredReviews.userReviews.find(x => x.reviewId === reviewId);

    if (review) {
      this.reviews.userReviews.find(x => x.reviewId === reviewId).totalThanks += 1;
    }

    if (positiveReview) {
      this.positiveReviews.userReviews.find(x => x.reviewId === reviewId).totalThanks += 1;
    }

    if (criticalReview) {
      this.criticalReviews.userReviews.find(x => x.reviewId === reviewId).totalThanks += 1;
    }

    if (filteredReview) {
      this.filteredReviews.userReviews.find(x => x.reviewId === reviewId).totalThanks += 1;
    }

    this.reviewIdsUpvoted.push(reviewId);

    return this.bexApiSource.upvotePropertyReview(variables).catch(error => {
      runInAction('recovering from bex api update property review call breakdown', () => {
        this.logger.logEvent(STORE_EVENTS.BEX_API_UPVOTE_PROPERTY_REVIEW_ERROR, variables, getErrorString(error));
      });
    });
  }

  @action
  public getCheapestPrice(): string {
    const firstRoomOffer: RoomViewModel = this.offers && this.offers.rooms && this.offers.rooms[0];

    return (
      firstRoomOffer &&
      firstRoomOffer.ratePlans &&
      firstRoomOffer.ratePlans[0] &&
      firstRoomOffer.ratePlans[0].price &&
      firstRoomOffer.ratePlans[0].price.lead
    );
  }

  @action
  public clearPoiData() {
    this.poiData = CurrentHotelStore.DEFAULTS.poiData;
  }
}
